import api from './api';
import type { ApiResponse } from '@/types';

// =============================================================================
// Staff tasks (/tasks — feature-flag tasks_enabled)
// =============================================================================

export type TaskPriority = 'low' | 'normal' | 'high';
export type TaskStatus = 'open' | 'done' | 'cancelled';
/**
 * What KIND of work item this is. `appointment` is the doctor's request that
 * the desk book this patient, and is the only type that reaches the front-desk
 * booking board — see App\Enums\TaskType. Optional so a payload from an older
 * backend still type-checks.
 */
export type TaskType = 'general' | 'appointment';

export interface TaskUserRef {
  id: number;
  full_name: string;
}

export interface TaskLeadRef {
  id: string;
  full_name: string;
}

export interface Task {
  id: string;
  title: string;
  description: string | null;
  priority: TaskPriority;
  type?: TaskType;
  purpose?: string | null;
  status: TaskStatus;
  /** Eligibility or structured workflow reason returned by the server. */
  blocked_reason?: string | null;
  cancellation_reason?: string | null;
  due_at: string | null;
  completed_at: string | null;
  completion_note?: string | null;
  completed_by?: TaskUserRef | null;
  lifecycle_version?: number;
  is_overdue: boolean;
  can_complete?: boolean;
  assignee?: TaskUserRef;
  creator?: TaskUserRef;
  lead?: TaskLeadRef | null;
  created_at: string;
  updated_at: string;
}

export type TaskView = 'my_work' | 'assigned_by_me' | 'completed';

export interface TaskFilters {
  view?: TaskView;
  status?: TaskStatus;
  priority?: TaskPriority;
  assigned_to?: number;
  overdue?: boolean;
  search?: string;
  page?: number;
  per_page?: number;
}

export interface CreateTaskData {
  title: string;
  description?: string | null;
  assigned_to?: number | null;
  lead_id?: string | null;
  due_at?: string | null;
  priority?: TaskPriority;
  type?: TaskType;
}

export interface UpdateTaskData extends Partial<CreateTaskData> { lifecycle_version?: number }

interface TaskListPayload {
  tasks: Task[];
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export const getTasks = async (filters: TaskFilters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  });
  const response = await api.get<ApiResponse<TaskListPayload>>(`/tasks?${params}`);
  return response.data.data;
};

export const getTaskAssignees = async () => {
  const response = await api.get<ApiResponse<TaskUserRef[]>>('/tasks/assignees');
  return response.data.data;
};

export const createTask = async (data: CreateTaskData) => {
  const response = await api.post<ApiResponse<Task>>('/tasks', data);
  return response.data.data;
};

export const updateTask = async (id: string, data: UpdateTaskData) => {
  const response = await api.put<ApiResponse<Task>>(`/tasks/${id}`, data);
  return response.data.data;
};

export const deleteTask = async (id: string) => {
  await api.delete(`/tasks/${id}`);
};

export interface TaskTransitionData { lifecycle_version?: number; completion_note?: string | null }

export const completeTask = async (id: string, data: TaskTransitionData = {}) => {
  const response = await api.post<ApiResponse<Task>>(`/tasks/${id}/complete`, data);
  return response.data.data;
};

export const reopenTask = async (id: string, data: TaskTransitionData = {}) => {
  const response = await api.post<ApiResponse<Task>>(`/tasks/${id}/reopen`, data);
  return response.data.data;
};

export const cancelTask = async (id: string, data: TaskTransitionData = {}) => {
  const response = await api.post<ApiResponse<Task>>(`/tasks/${id}/cancel`, data);
  return response.data.data;
};

export interface TaskHistoryEvent {
  id: string | number;
  event: 'created' | 'updated' | 'deleted' | 'rescheduled';
  actor: TaskUserRef | null;
  created_at: string;
  changes: Record<string, { old: string | number | boolean | TaskUserRef | null; new: string | number | boolean | TaskUserRef | null }>;
}

export const getTaskHistory = async (id: string, page = 1) => {
  const response = await api.get<ApiResponse<{ history: TaskHistoryEvent[]; pagination: TaskListPayload['pagination'] }>>(`/tasks/${id}/history?page=${page}`);
  return response.data.data;
};
